import { FC, memo, useCallback, useEffect, useState } from 'react';
import { SingleValue } from 'react-select';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';

import { useDeepCompareMemo, userRolesMap } from '@clatter/platform';
import { AccessGuard, DropdownSelect } from '../../../components';
import { authSelectors, setSelectedThemeId } from '../../../store';
import { useGetUserRootFoldersQuery, useGetFolderAssetsQuery, useGetAssetThumbnailUrlQuery } from '../../../services';
import { sortByDate } from '../../../helpers/sorting';

export const NONE_THEME_VALUE = 'none';

interface ThemeOptionItem extends IDropdownSelectOptionItem {
  assetId?: number;
  thumbnailUrl?: string;
  isDefault?: boolean;
}

interface Props {
  selectedThemeId?: number | null;
  defaultThemeId?: number | null;
  isLoadingDefault?: boolean;
  viewStateKey?: string;
  onChange?: (theme: SingleValue<ThemeOptionItem>) => void;
}

const StyledOptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  user-select: none;
  pointer-events: none;
`;

const StyledThumbnail = styled.div<{ $url?: string }>`
  width: 40px;
  height: 30px;
  border-radius: 4px;
  background-color: #f0f0f0;
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const StyledLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledLabel = styled.span<{ $isDefault?: boolean }>`
  font-size: 14px;
  font-weight: ${({ $isDefault }) => ($isDefault ? '600' : '400')};
`;

const StyledDefaultText = styled.span`
  font-size: 10px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const selectStyles = {
  control: (provided: any) => ({
    ...provided,
    cursor: 'pointer',
  }),
  option: (provided: any) => ({
    ...provided,
    cursor: 'pointer',
  }),
};

const ThemeOptionLabel = memo<{ option: ThemeOptionItem }>(({ option }) => {
  const { data: thumbnailUrl } = useGetAssetThumbnailUrlQuery(
    option.assetId != null ? { assetId: option.assetId } : skipToken,
  );
  const url = (option.assetId && thumbnailUrl) || option.thumbnailUrl;

  return (
    <StyledOptionContainer>
      <StyledThumbnail $url={url} />
      <StyledLabelContainer>
        <StyledLabel $isDefault={option.isDefault}>{option.label}</StyledLabel>
        {option.isDefault && <StyledDefaultText>Default</StyledDefaultText>}
      </StyledLabelContainer>
    </StyledOptionContainer>
  );
});

const formatOptionLabel = (option: ThemeOptionItem) => <ThemeOptionLabel option={option} />;

export const SlidesThemeSelector: FC<Props> = ({
  selectedThemeId: selectedThemeIdProp,
  defaultThemeId,
  isLoadingDefault,
  viewStateKey,
  onChange,
}) => {
  const dispatch = useDispatch();

  const storeThemeId = useSelector((state: any) =>
    viewStateKey ? state.viewState?.byKey?.[viewStateKey]?.selectedThemeId ?? null : null,
  );
  const hasStoreEntry = useSelector((state: any) =>
    viewStateKey ? !!state.viewState?.byKey?.[viewStateKey] : false,
  );
  const selectedThemeId = selectedThemeIdProp !== undefined ? selectedThemeIdProp : storeThemeId;

  const defaultOnChange = useCallback(
    (theme: SingleValue<ThemeOptionItem>) => {
      if (!viewStateKey) return;
      const themeId = theme?.value && theme.value !== NONE_THEME_VALUE ? Number(theme.value) : null;
      dispatch(setSelectedThemeId({ viewStateKey, themeId }));
    },
    [dispatch, viewStateKey],
  );

  const handleOnChange = onChange || defaultOnChange;

  // @todo this should be from themes endpoint
  const { data: userRootFolders, isLoading: isRootFoldersLoading } = useGetUserRootFoldersQuery();
  const activeUser = useSelector(authSelectors.getUserAuthState)?.user;

  const isAdmin = useDeepCompareMemo(
    () =>
      AccessGuard.check({
        allowedRoles: [userRolesMap.clatterAdmin, userRolesMap.ngAdmin, userRolesMap.systemAdmin],
        user: activeUser,
      }),
    [activeUser],
  );

  const themeFolderId = useDeepCompareMemo(
    () => userRootFolders?.find((item) => item?.slug === 'themes')?.id,
    [userRootFolders],
  );

  const { data: themeAssets, isLoading: isFoldersLoading } = useGetFolderAssetsQuery(
    themeFolderId ? { parentId: themeFolderId } : skipToken,
  );

  const themesOptions: ThemeOptionItem[] = useDeepCompareMemo(() => {
    // make sure themes are sorted so oldest one will be marked as default
    if (!themeAssets || !Array.isArray(themeAssets)) {
      return [];
    }

    const sortedAssets = sortByDate([...themeAssets], 'createdAt', 'asc');

    const mappedAssetsToThemes = sortedAssets.map((theme, index) => ({
      value: theme.currentVersion?.ID?.toString(),
      label: theme.name,
      assetId: theme.id,
      thumbnailUrl: theme.thumbnailUrl,
      isDefault: index === 0, // oldest theme is default (for now, it should be from theme pro when api will be ready)
    }));

    if (isAdmin) return [{ value: NONE_THEME_VALUE, label: 'None' }, ...mappedAssetsToThemes];

    return mappedAssetsToThemes;
  }, [themeAssets, isAdmin]);

  const isLoading = isRootFoldersLoading || isFoldersLoading;

  // track if initial default selection has been made
  const [hasInitialized, setHasInitialized] = useState(false);

  // find the selected option from themesOptions based on selectedThemeId
  const selectedOption = useDeepCompareMemo(() => {
    if (isAdmin && selectedThemeId === null) {
      return (hasInitialized || hasStoreEntry) ? themesOptions.find((option) => option.value === NONE_THEME_VALUE) || null : null;
    }
    return themesOptions.find((option) => option.value === `${selectedThemeId}`) || null;
  }, [selectedThemeId, themesOptions, hasInitialized]);

  const handleChange = (newValue: SingleValue<ThemeOptionItem>, _actionMeta: any) => {
    setHasInitialized(true);
    handleOnChange(newValue);
  };

  useEffect(() => {
    if (!hasInitialized && !selectedThemeId && !hasStoreEntry && !isLoadingDefault && themesOptions.length > 0) {
      // use defaultThemeId from presentation if provided, otherwise fall back to first default theme
      const defaultOption = defaultThemeId
        ? themesOptions.find((theme) => theme.value === `${defaultThemeId}`)
        : themesOptions.find((theme) => theme.isDefault);

      if (defaultOption) {
        setHasInitialized(true);
        handleOnChange(defaultOption);
      }
    }
  }, [selectedThemeId, defaultThemeId, isLoadingDefault, handleOnChange, themesOptions, hasInitialized, hasStoreEntry]);

  return (
      <DropdownSelect
        value={selectedOption}
        options={themesOptions}
        onChange={handleChange}
        formatOptionLabel={formatOptionLabel}
        placeholder="Select a theme"
        isLoading={isLoading}
        isSearchable={false}
        styles={selectStyles}
      />
  );
};
